import React, { useEffect, useState } from 'react'
import Layout from '../../../components/Layout/Layout'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { Modal } from 'react-bootstrap'
import MainNewConference from './NewConference'
import { useAuth } from '../../../context/auth'
import useIsMobile from '../../../hooks/useIsMobile'

const MainConferenceList = () => {
  const navigate = useNavigate()
  const [auth] = useAuth()
  const userId = auth?.user?.id
  const groupId = auth?.user?.group
  const isMobile = useIsMobile()

  const [conferences, setConferences] = useState([])
  const [loading, setLoading] = useState(true)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingAttendance, setEditingAttendance] = useState(null)

  useEffect(() => {
    const fetchConferences = async () => {
      try {
        setLoading(true)
        const { data } = await axios.get('/api/v1/main/conference/lists', {
          params: { groupId: groupId },
        })
        if (data.success) {
          setConferences(data.data)
        }
      } catch (error) {
        console.error('Failed to fetch conferences:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchConferences()
  }, [groupId])

  const handleOpenModal = () => {
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
  }

  const handleFormSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        userId,
        groupId,
      }

      const { data } = await axios.post(
        '/api/v1/main/conference/add-event',
        payload
      )
      if (data.success) {
        setConferences((prev) => [data.data, ...prev])
        handleCloseModal()
      }
    } catch (error) {
      console.error('Failed to add conference:', error)
    }
  }

  const handleAttendanceUpdate = async (id, newAttendance) => {
    try {
      const { data } = await axios.put(
        `/api/v1/main/conference/update-attendance/${id}`,
        { attendance: newAttendance }
      )
      if (data.success) {
        setConferences((prev) =>
          prev.map((conf) =>
            conf._id === id ? { ...conf, attendance: newAttendance } : conf
          )
        )
      }
    } catch (error) {
      console.error('Failed to update attendance:', error)
    }
  }

  const handleAttendanceDoubleClick = (id) => {
    setEditingAttendance(id)
  }

  const handleAttendanceChange = (id, value) => {
    setConferences((prev) =>
      prev.map((conf) =>
        conf._id === id ? { ...conf, attendance: value } : conf
      )
    )
  }

  const handleAttendanceBlur = (id, value) => {
    const originalValue = conferences.find(
      (conf) => conf._id === id
    )?.attendance
    if (parseInt(value, 10) !== originalValue) {
      handleAttendanceUpdate(id, value)
    }
    setEditingAttendance(null)
  }

  const handleAttendanceKeyDown = (id, event, value) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleAttendanceBlur(id, value)
    }
  }

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { data } = await axios.put(
        `/api/v1/main/conference/update-status/${id}`,
        { status: newStatus }
      )
      if (data.success) {
        setConferences((prev) =>
          prev.map((conf) =>
            conf._id === id ? { ...conf, status: newStatus } : conf
          )
        )
      }
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const toggleStatus = (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 2 : 1
    const confirmChange = window.confirm('Confirm Update Status?')

    if (confirmChange) {
      handleStatusChange(id, newStatus)
    }
  }

  const handleReturnHome = () => navigate('/main/dashboard')

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  return (
    <Layout title={'Conference'}>
      <div className='pt-3 pb-5 mx-3'>
        {/* Header */}
        <div className='d-flex mb-2 justify-content-between align-items-center'>
          <h5 className='mb-0' style={{ color: '#556B2F' }}>
            Conference
          </h5>

          <div className='d-flex ms-auto'>
            <button
              type='button'
              className='btn btn-success btn-sm me-2'
              onClick={handleOpenModal}
            >
              <i className='bi bi-plus-circle'></i>
              {!isMobile && ' Conference'}
            </button>
            <button
              type='button'
              className='btn btn-outline-secondary btn-sm'
              onClick={handleReturnHome}
            >
              <i className='bi bi-house-fill'></i>
            </button>
          </div>
        </div>

        {/* Desktop Table View */}
        {!isMobile && (
          <div className='table-responsive'>
            <table className='table'>
              <thead>
                <tr>
                  <th scope='col'>Date</th>
                  <th scope='col'>Event</th>
                  <th scope='col' className='text-center'>
                    Attendance
                  </th>
                  <th scope='col' className='text-end'>
                    Total Sales
                  </th>
                  <th scope='col' className='text-center'>
                    Status
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className='text-center py-4'>
                      <div
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                      >
                        <span className='visually-hidden'>Loading...</span>
                      </div>
                      Loading...
                    </td>
                  </tr>
                ) : conferences.length > 0 ? (
                  conferences.map((conference) => (
                    <tr key={conference._id}>
                      <td>{formatDate(conference.recordDate)}</td>
                      <td>{conference.event}</td>
                      <td
                        className='text-center'
                        onDoubleClick={() =>
                          handleAttendanceDoubleClick(conference._id)
                        }
                      >
                        {editingAttendance === conference._id ? (
                          <input
                            type='number'
                            value={conference.attendance || 0}
                            onChange={(e) =>
                              handleAttendanceChange(
                                conference._id,
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleAttendanceBlur(
                                conference._id,
                                e.target.value
                              )
                            }
                            onKeyDown={(e) =>
                              handleAttendanceKeyDown(
                                conference._id,
                                e,
                                e.target.value
                              )
                            }
                            className='form-control form-control-sm'
                            style={{ width: '80px', margin: '0 auto' }}
                            autoFocus
                          />
                        ) : (
                          conference.attendance || 0
                        )}
                      </td>
                      <td className='text-end'>
                        {formatCurrency(conference.totalSales)} (
                        {conference.totalQty || 0})
                      </td>
                      <td
                        className='text-center'
                        onClick={() =>
                          toggleStatus(conference._id, conference.status)
                        }
                        style={{
                          color: conference.status === 1 ? 'green' : 'grey',
                          cursor: 'pointer',
                        }}
                      >
                        {conference.status === 1 ? 'Active' : 'Inactive'}
                      </td>
                      <td className='text-end'>
                        <button
                          className='btn btn-sm btn-success me-2'
                          onClick={() => navigate(`sale/${conference._id}`)}
                        >
                          Sale
                        </button>
                        <button
                          className='btn btn-sm btn-primary'
                          onClick={() => navigate(`record/${conference._id}`)}
                        >
                          Record
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className='text-center'>
                      Tiada rekod
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile List View */}
        {isMobile && (
          <div className='mt-2'>
            {loading ? (
              <div className='text-center py-4'>
                <div
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                >
                  <span className='visually-hidden'>Loading...</span>
                </div>
                Loading...
              </div>
            ) : conferences.length > 0 ? (
              conferences.map((conference) => (
                <div
                  key={conference._id}
                  className='py-2'
                  style={{ borderBottom: '1px solid #dee2e6' }}
                >
                  {/* Row 1: Event & Status */}
                  <div className='d-flex justify-content-between align-items-center'>
                    <span className='fw-semibold' style={{ color: '#0c432a' }}>
                      {conference.event}
                    </span>
                    <span
                      style={{
                        color: conference.status === 1 ? 'green' : 'grey',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                      }}
                      onClick={() =>
                        toggleStatus(conference._id, conference.status)
                      }
                    >
                      {conference.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Row 2: Date & Sales */}
                  <div className='d-flex justify-content-between align-items-center mt-1'>
                    <small className='text-muted'>
                      {formatDate(conference.recordDate)}
                    </small>
                    <small>
                      RM {formatCurrency(conference.totalSales)} (
                      {conference.totalQty || 0})
                    </small>
                  </div>

                  {/* Row 3: Attendance & Buttons */}
                  <div className='d-flex justify-content-between align-items-center mt-2'>
                    <div>
                      <small className='text-muted'>Att: </small>
                      {editingAttendance === conference._id ? (
                        <input
                          type='number'
                          value={conference.attendance || 0}
                          onChange={(e) =>
                            handleAttendanceChange(
                              conference._id,
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handleAttendanceBlur(conference._id, e.target.value)
                          }
                          onKeyDown={(e) =>
                            handleAttendanceKeyDown(
                              conference._id,
                              e,
                              e.target.value
                            )
                          }
                          className='form-control form-control-sm d-inline-block'
                          style={{ width: '60px' }}
                          autoFocus
                        />
                      ) : (
                        <span
                          onClick={() =>
                            handleAttendanceDoubleClick(conference._id)
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          {conference.attendance || 0}
                        </span>
                      )}
                    </div>
                    <div className='d-flex gap-2'>
                      <button
                        className='btn btn-success btn-sm'
                        onClick={() => navigate(`sale/${conference._id}`)}
                      >
                        Sale
                      </button>
                      <button
                        className='btn btn-primary btn-sm'
                        onClick={() => navigate(`record/${conference._id}`)}
                      >
                        Record
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className='text-center py-4 text-muted'>Tiada rekod</div>
            )}
          </div>
        )}
      </div>

      <Modal show={isModalVisible} onHide={handleCloseModal} centered>
        <Modal.Body>
          <MainNewConference
            onFormSubmit={handleFormSubmit}
            onCancel={handleCloseModal}
          />
        </Modal.Body>
      </Modal>
    </Layout>
  )
}

export default MainConferenceList
